import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList,
  moveItemInArray,
  transferArrayItem } from '@angular/cdk/drag-drop';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import {
  filter,
  firstValueFrom,
  from,
  Observable,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthState } from '../../../core/authentication/auth.state';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { LearningUnitEntity } from '../../../learning-unit/learning-unit-entity';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import { SubjectEntity } from '../../../subject/subject.entity';
import {
  LearningSectionEntity,
  SectionRowButtonBarData,
  SectionRowContainerData,
  SectionRowData
} from '../../learning-section-entity';
import { LearningSectionLayoutEntity } from '../../learning-section-layout/learning-section-layout-entity';
import { LearningSectionLayoutEntityService } from '../../learning-section-layout/services/learning-section-layout.entity.service';
import { LearningSectionContainerEntityService } from '../services/learning-section-container.entity.service';
import {
  normalizeEntitiesToRelations,
  normalizeManyRelations,
  normalizeOneRelation,
} from '../../../core/decorators/relation.decorator';
import { LearningSectionContainerEntity } from '../learning-section-container-entity';
import {
  EntityStateModel,
  InvalidateLoadedQueries,
} from '../../../core/state/entity.state';
import { LearningSectionEntityService } from '../../services/learning-section.entity.service';
import { RelationRef } from '../../../core/types/entity-types';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData,
} from '../../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import {
  messageTypes,
  WidgetMessageService,
} from '../../../core/services/widget-message.service';
import { FormDialogLearningSectionContainerContentComponent } from '../form-dialog-learning-section-container-content/form-dialog-learning-section-container-content.component';
import { ExplanationContentEntityService } from '../../../explanation/content/services/explanation-content.entity.service';
import { TestEntityService } from '../../../test/services/test.entity.service';
import { SequenceEntityService } from '../../../sequence/services/sequence.entity.service';
import { ExplanationEntityService } from '../../../explanation/services/explanation.entity.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConsumerEntityService } from '../../../consumer/services/consumer.entity.service';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  FormDialogButtonBarItemComponent
} from '../form-dialog-button-bar-item/form-dialog-button-bar-item.component';
import { ButtonBarItemData } from '../../learning-section-entity';

export interface ButtonBarItem {
  label: string
  contentType?: string | null
  contentId?: string | null
}

export interface ContainerRow {
  type: 'containers'
  containers: (LearningSectionContainerEntity | null)[]
}

export interface ButtonBarRow {
  type: 'button-bar'
  buttonCount: number
  items: ButtonBarItem[]
}

export type SectionRow = ContainerRow | ButtonBarRow;

@Component({
  selector: 'pixl-form-dialog-learning-section-container',
  imports: [
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    MatButton,
    MatButtonModule,
    MatIcon,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatDialogModule,
    MatMenuModule,
    MatInputModule,
    MatTooltipModule,
    TranslocoModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-dialog-learning-section-container.component.html',
  styleUrl: './form-dialog-learning-section-container.component.scss',
})
export class FormDialogLearningSectionContainerComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<FormDialogLearningSectionContainerComponent>>(
      MatDialogRef,
    );

  data? = inject<Partial<LearningSectionEntity>>(MAT_DIALOG_DATA);

  form: FormGroup;
  learningSectionLayouts$!: Observable<LearningSectionLayoutEntity[]>;

  private store = inject(Store);
  private dialog = inject(MatDialog);
  private widgetService = inject(WidgetMessageService);
  private cdr = inject(ChangeDetectorRef);

  private learningSectionService = inject(LearningSectionEntityService);
  private learningSectionLayoutService = inject(
    LearningSectionLayoutEntityService,
  );

  private learningSectionContainerService = inject(
    LearningSectionContainerEntityService,
  );

  private explanationContentService = inject(ExplanationContentEntityService);
  private testService = inject(TestEntityService);
  private sequenceService = inject(SequenceEntityService);
  private explanationService = inject(ExplanationEntityService);
  private consumerService = inject(ConsumerEntityService);
  private translocoService = inject(TranslocoService);
  private snackBar = inject(MatSnackBar);

  public showContainerId = signal<boolean>(false);

  containerContentData: Record<
    string,
    { name: string, type: string } | undefined
  > = {};

  buttonItemContentData: Record<string, { name: string, type: string } | undefined> = {};
  sectionRows: SectionRow[] = [];
  isDraggingContainer = false;

  selectedLayoutOption!: LearningSectionLayoutEntity;
  columnNumber!: number;

  constructor() {
    const data = this.data;

    this.form = this.fb.group({
      id: [data?.id || null],
      name: [data?.name || ''],
      title: [data?.title || ''],
      learningSectionType: [data?.learningSectionType || ''],
      showCopyright: [data?.showCopyright || false],
      version: [data?.version || 1],
      consumer: [
        normalizeOneRelation<ConsumerEntity>(
          data?.consumer as RelationRef<ConsumerEntity>[],
        ),
      ],
      subjects: [
        normalizeManyRelations<SubjectEntity>(
          data?.subjects as RelationRef<SubjectEntity>[],
        ),
      ],
      learningUnits: [
        normalizeManyRelations<LearningUnitEntity>(
          data?.learningUnits as RelationRef<LearningUnitEntity>[],
        ),
      ],
      learningSectionLayout: [
        normalizeOneRelation<LearningSectionLayoutEntity>(
          data?.learningSectionLayout as RelationRef<LearningSectionLayoutEntity>[],
        ),
      ],
      learningSectionContainers: [
        normalizeManyRelations<LearningSectionContainerEntity>(
          data?.learningSectionContainers as RelationRef<LearningSectionContainerEntity>[],
        ),
      ],
    });
  }

  ngOnInit() {
    const learningSectionLayoutQuery = { sort: 'name' };

    this.learningSectionLayouts$ = from(
      this.learningSectionLayoutService.ensureLoaded(
        learningSectionLayoutQuery,
      ),
    ).pipe(
      switchMap(() =>
        this.store.select(
          this.learningSectionLayoutService.allByQuery(
            learningSectionLayoutQuery,
          ),
        ),
      ),
      tap((layouts) => {
        const control = this.form.get('learningSectionLayout');

        // Set default layout ID if missing
        if (layouts.length && !control?.value) {
          control?.setValue(layouts[0].id);
        }

        // Resolve selectedLayoutOption from form value
        const selectedId = control?.value;
        const selectedLayout = layouts.find((l) => l.id === selectedId);

        if (selectedLayout) {
          this.selectedLayoutOption = selectedLayout;
          this.setLayout(selectedLayout);
        }
      }),
    );

    this.preloadContainersWithContent().then(async () => {
      if (this.data?.sectionRows?.length) {
        this.deserializeSectionRows(this.data.sectionRows);
        if (this.normalizeFullWidthRows()) {
          await this.updateLearningSection();
        }
      }
      else {
        this.rebuildContainerRows();
      }
      await this.loadButtonItemContentData();
      this.cdr.markForCheck();
    }).catch((err) => console.error(err));
  }

  get learningSectionContainers(): LearningSectionContainerEntity[] {
    const selectedRefs =
      this.form.get('learningSectionContainers')?.value ?? [];
    const containerState = this.store.selectSnapshot(
      this.learningSectionContainerService.stateClass,
    ) as EntityStateModel<LearningSectionContainerEntity>;

    return (
      selectedRefs as (string | RelationRef<LearningSectionContainerEntity>)[]
    )
      .map((ref) => {
        const id = typeof ref === 'string' ? ref : ref.id;
        return containerState.entities[id];
      })
      .filter((c): c is LearningSectionContainerEntity => !!c);
  }

  rebuildContainerRows(): void {
    const containers = this.learningSectionContainers;
    const cols = this.columnNumber || 1;
    const containerRows: ContainerRow[] = [];
    let i = 0;
    while (i < containers.length) {
      if (containers[i].fullWidth) {
        containerRows.push({ type: 'containers', containers: [containers[i]] });
        i++;
      }
      else {
        const row: (LearningSectionContainerEntity | null)[] = [];
        for (let j = 0; j < cols && i < containers.length; j++) {
          if (containers[i].fullWidth) {
            break;
          }
          row.push(containers[i]);
          i++;
        }
        if (row.length > 0) {
          containerRows.push({ type: 'containers', containers: this.padContainersToMaxCols(row, cols) });
        }
      }
    }

    // Preserve existing button-bar rows at their positions
    const existingButtonBars = this.sectionRows.filter(
      (r): r is ButtonBarRow => r.type === 'button-bar'
    );

    // Rebuild: container rows first, then re-insert button-bars at their previous indices
    const newRows: SectionRow[] = [...containerRows];
    for (const bl of existingButtonBars) {
      const prevIndex = this.sectionRows.indexOf(bl);
      const insertAt = Math.min(prevIndex, newRows.length);
      newRows.splice(insertAt, 0, bl);
    }

    this.sectionRows = newRows;
  }

  private async preloadContainersWithContent() {
    const selectedRefs = this.form.get('learningSectionContainers')?.value as
      | string[] |
      RelationRef<LearningSectionContainerEntity>[];

    if (!selectedRefs?.length) return;

    // 1. Get initial snapshots once to avoid repeated overhead
    const containerState = this.store.selectSnapshot(
      this.learningSectionContainerService.stateClass,
    ) as EntityStateModel<LearningSectionContainerEntity>;

    // 2. Map all containers to a list of Promises to run them in parallel
    const loadPromises = selectedRefs.map(async (ref) => {
      const id = typeof ref === 'string' ? ref : ref.id;
      let container = containerState.entities[id];

      if (!container) return;

      // Ensure content relation is loaded
      if (!container.content || !('id' in container.content)) {
        await this.learningSectionContainerService.ensureSingleLoaded(id, {
          include: 'content',
        });
        // Refresh local container ref after loading
        container = (
          this.store.selectSnapshot(
            this.learningSectionContainerService.stateClass,
          ) as EntityStateModel<LearningSectionContainerEntity>
        ).entities[id];
      }

      const contentRef = container?.content;
      if (!contentRef || !('id' in contentRef && 'type' in contentRef)) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const serviceMap: Record<string, any> = {
        'explanation-contents': this.explanationContentService,
        'tests': this.testService,
        'sequences': this.sequenceService,
        'explanations': this.explanationService,
        'learning-sections': this.learningSectionService
      };

      const service = serviceMap[contentRef.type];
      if (service) {
        const contentState = this.store.selectSnapshot(
          service.stateClass,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ) as EntityStateModel<any>;
        if (!contentState.entities[contentRef.id]) {
          await service.ensureSingleLoaded(contentRef.id, {});
        }

        const finalState = this.store.selectSnapshot(
          service.stateClass,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ) as EntityStateModel<any>;
        const loadedEntity = finalState.entities[contentRef.id];

        // Update local tracking object
        this.containerContentData[id] = {
          name: loadedEntity?.name ?? '',
          type: contentRef.type,
        };
      }
    });

    // 3. Wait for all requests to finish in parallel
    await Promise.all(loadPromises);

    // 4. Update UI once
    this.cdr.markForCheck();
  }

  private async loadButtonItemContentData(): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const serviceMap: Record<string, any> = {
      'explanation-contents': this.explanationContentService,
      'tests': this.testService,
      'sequences': this.sequenceService,
      'explanations': this.explanationService,
      'learning-sections': this.learningSectionService
    };
    const loadPromises: Promise<void>[] = [];
    this.sectionRows.forEach((row, rowIdx) => {
      if (row.type !== 'button-bar') return;
      row.items.forEach((item, itemIdx) => {
        const key = `${rowIdx}-${itemIdx}`;
        if (!item.contentType || !item.contentId) {
          this.buttonItemContentData[key] = undefined;
          return;
        }
        const service = serviceMap[item.contentType];
        if (!service) return;
        loadPromises.push((async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const contentState = this.store.selectSnapshot(service.stateClass) as EntityStateModel<any>;
          if (!contentState.entities[item.contentId!]) {
            await service.ensureSingleLoaded(item.contentId, {});
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const finalState = this.store.selectSnapshot(service.stateClass) as EntityStateModel<any>;
          const loadedEntity = finalState.entities[item.contentId!];
          this.buttonItemContentData[key] = {
            name: loadedEntity?.name ?? '',
            type: item.contentType!
          };
        })());
      });
    });
    await Promise.all(loadPromises);
  }

  async addContainer() {
    const learningSectionId = this.form.get('id')?.value;
    if (!learningSectionId) return;

    await this.learningSectionService.ensureSingleLoaded(learningSectionId);

    const state = this.store.selectSnapshot(
      this.learningSectionService.stateClass,
    ) as EntityStateModel<LearningSectionEntity>;

    const learningSection = state.entities[learningSectionId];
    if (!learningSection) return;

    const count = this.columnNumber || 1;
    const createdContainers: LearningSectionContainerEntity[] = [];

    for (let i = 0; i < count; i++) {
      const newContainer: Partial<LearningSectionContainerEntity> = {
        title: this.translocoService.translate('learningSectionContainer.defaultContainerTitle'),
        learningSections: normalizeEntitiesToRelations(
          [learningSection],
          'learning-sections',
        ),
        fullWidth: false,
        version: 1,
      };

      const createdContainer =
        await this.learningSectionContainerService.create(newContainer);
      createdContainers.push(createdContainer);
    }

    // Update form control with new container IDs
    const currentIds: string[] = this.form.get('learningSectionContainers')?.value ?? [];
    this.form.get('learningSectionContainers')?.setValue([
      ...currentIds,
      ...createdContainers.map(c => c.id)
    ]);
    await this.updateLearningSection();

    // Add all new containers as one row (preserve existing row structure)
    this.sectionRows.push({ type: 'containers', containers: createdContainers } as ContainerRow);
    await this.updateLearningSection();
  }

  async createContainerAtSlot(rowIndex: number, slotIndex: number): Promise<void> {
    const learningSectionId = this.form.get('id')?.value;
    if (!learningSectionId) return;
    await this.learningSectionService.ensureSingleLoaded(learningSectionId);
    const state = this.store.selectSnapshot(
      this.learningSectionService.stateClass
    ) as EntityStateModel<LearningSectionEntity>;
    const learningSection = state.entities[learningSectionId];
    if (!learningSection) return;

    const newContainer: Partial<LearningSectionContainerEntity> = {
      title: this.translocoService.translate('learningSectionContainer.defaultContainerTitle'),
      learningSections: normalizeEntitiesToRelations(
        [learningSection],
        'learning-sections'
      ),
      fullWidth: false,
      version: 1,
    };
    const createdContainer = await this.learningSectionContainerService.create(newContainer);
    this.learningSectionContainers.push(createdContainer);

    const currentIds: string[] =
      this.form.get('learningSectionContainers')?.value ?? [];
    this.form
      .get('learningSectionContainers')
      ?.setValue([...currentIds, createdContainer.id]);
    await this.updateLearningSection();

    const row = this.sectionRows[rowIndex];
    if (row && row.type === 'containers') {
      row.containers[slotIndex] = createdContainer;
    }
    await this.updateLearningSection();
  }

  async addButtonBar(): Promise<void> {
    const defaultCount = 3;
    const items: ButtonBarItem[] = Array.from(
      { length: defaultCount },
      () => ({ label: this.translocoService.translate('learningSectionContainer.defaultButtonLabel'), contentType: null, contentId: null })
    );
    const newRow: ButtonBarRow = {
      type: 'button-bar',
      buttonCount: defaultCount,
      items
    };
    this.sectionRows.push(newRow);
    await this.updateLearningSection();
  }

  async updateButtonCount(row: ButtonBarRow, count: number, event?: Event): Promise<void> {
    const clamped = Math.max(1, Math.min(5, count));
    const oldCount = row.buttonCount;

    if (clamped < row.items.length) {
      // Reset the input to the old value immediately to prevent visual change before confirmation
      if (event?.target) {
        (event.target as HTMLInputElement).value = String(oldCount);
      }

      const confirmed = await firstValueFrom(
        this.dialog
          .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
            data: {
              name: '',
              headerText: this.translocoService.translate('learningSectionContainer.reduceButtonCountHeader'),
              afterNameText: this.translocoService.translate('learningSectionContainer.reduceButtonCountWarning')
            }
          })
          .afterClosed()
      );

      if (!confirmed) return;
    }

    row.buttonCount = clamped;
    while (row.items.length < clamped) {
      row.items.push({ label: this.translocoService.translate('learningSectionContainer.defaultButtonLabel'), contentType: null, contentId: null });
    }
    while (row.items.length > clamped) {
      row.items.pop();
    }
    await this.updateLearningSection();
  }

  async removeButtonBar(row: ButtonBarRow): Promise<void> {
    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: this.translocoService.translate('learningSectionContainer.elementButtonBar'),
            headerText: this.translocoService.translate('learningSectionContainer.deleteButtonBarHeader'),
            afterNameText: this.translocoService.translate('learningSectionContainer.deleteConfirmAfterName')
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    const idx = this.sectionRows.indexOf(row);
    if (idx >= 0) {
      this.sectionRows.splice(idx, 1);
      await this.updateLearningSection();
    }
  }

  async removeButtonItem(row: ButtonBarRow, index: number): Promise<void> {
    if (row.items.length <= 1) return;

    const item = row.items[index];
    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: item.label || 'Button',
            headerText: this.translocoService.translate('learningSectionContainer.deleteButtonItemHeader'),
            afterNameText: this.translocoService.translate('learningSectionContainer.deleteConfirmAfterName')
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    row.items.splice(index, 1);
    row.buttonCount = row.items.length;
    await this.updateLearningSection();
  }

  async removeContainer(container: LearningSectionContainerEntity) {
    if (!container?.id) {
      console.error('ID is undefined!', container);
      return;
    }

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(container.title),
            headerText: this.translocoService.translate('learningSectionContainer.deleteElementContainer'),
            afterNameText: this.translocoService.translate('learningSectionContainer.reallyDelete')
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(
      this.learningSectionContainerService.delete(container.id),
    );
    this.store.dispatch(new InvalidateLoadedQueries());

    const currentContainers =
      this.form.get('learningSectionContainers')?.value ?? [];

    // Remove the deleted container by ID
    const updatedContainers = (
      currentContainers as (
        | string |
        RelationRef<LearningSectionContainerEntity>
      )[]
    ).filter((c) => (typeof c === 'string' ? c : c.id) !== container.id);

    this.form.get('learningSectionContainers')?.setValue(updatedContainers);

    // Replace the deleted container with null (keep the gap) and clean up fully empty rows
    for (const row of this.sectionRows) {
      if (row.type === 'containers') {
        const idx = row.containers.findIndex(c => c?.id === container.id);
        if (idx >= 0) {
          row.containers[idx] = null;
          break;
        }
      }
    }
    this.sectionRows = this.sectionRows.filter(
      r => r.type !== 'containers' || r.containers.some(c => c !== null)
    );

    await this.updateLearningSection();

    this.showMessage(this.translocoService.translate('learningSectionContainer.deleteElementContainerSuccessful'));
  }

  async dropRow(event: CdkDragDrop<SectionRow[]>) {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.sectionRows, event.previousIndex, event.currentIndex);

    const flatContainers = this.sectionRows
      .filter((r): r is ContainerRow => r.type === 'containers')
      .flatMap(r => r.containers)
      .filter((c): c is LearningSectionContainerEntity => c !== null);
    const normalizedContainers = flatContainers.map(c => ({
      id: c.id!,
      type: 'learning-section-containers'
    }));

    this.form.get('learningSectionContainers')?.setValue(normalizedContainers);
    this.buttonItemContentData = {};
    await this.loadButtonItemContentData();
    this.cdr.markForCheck();
    await this.updateLearningSection();
  }

  getContainerDropListIds(): string[] {
    return this.sectionRows
      .map((_, i) => `container-row-${i}`)
      .filter((_, i) => this.sectionRows[i].type === 'containers');
  }

  getConnectedDropListIds(currentIndex: number): string[] {
    const currentRow = this.sectionRows[currentIndex];
    if (currentRow && this.isFullWidthRow(currentRow)) {
      return [];
    }

    return this.getContainerDropListIds()
      .filter(id => id !== `container-row-${currentIndex}`)
      .filter(id => {
        const idx = parseInt(id.replace('container-row-', ''), 10);
        const row = this.sectionRows[idx];
        if (row?.type !== 'containers') return false;
        if (this.isFullWidthRow(row)) return false;
        const usedSlots = row.containers.filter(c => c !== null).length;
        return usedSlots < this.columnNumber;
      });
  }

  updateContainerPreviewWidth(event: { container: CdkDropList }): void {
    requestAnimationFrame(() => {
      const preview = document.querySelector('.cdk-drag-preview.learning-section-grid-item') as HTMLElement;
      if (!preview) return;

      const rowInner = event.container.element.nativeElement;
      const placeholder = rowInner.querySelector('.cdk-drag-placeholder:not(.empty-slot)') as HTMLElement;
      if (placeholder) {
        const rect = placeholder.getBoundingClientRect();
        if (rect.width > 0) {
          preview.style.width = rect.width + 'px';
        }
      }
    });
  }

  isRowFull(rowIndex: number): boolean {
    const row = this.sectionRows[rowIndex];
    if (row?.type !== 'containers') return false;
    if (this.isFullWidthRow(row)) return true;
    const usedSlots = row.containers.filter(c => c !== null).length;
    return usedSlots >= this.columnNumber;
  }

  getButtonBarDropListIds(): string[] {
    return this.sectionRows
      .map((_, i) => `button-bar-${i}`)
      .filter((_, i) => this.sectionRows[i].type === 'button-bar');
  }

  getConnectedButtonBarDropListIds(currentIndex: number): string[] {
    return this.getButtonBarDropListIds()
      .filter(id => id !== `button-bar-${currentIndex}`);
  }

  async dropButtonItem(event: CdkDragDrop<ButtonBarItem[]>, targetRow: ButtonBarRow): Promise<void> {
    if (event.previousContainer === event.container) {
      if (event.previousIndex === event.currentIndex) return;
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    }
    else {
      // Cross-bar transfer: check max constraint on target
      const sourceData = event.previousContainer.data;
      const targetData = event.container.data;

      // Target must not exceed 5 buttons
      if (targetData.length >= 5) {
        this.snackBar.open(
          this.translocoService.translate('learningSectionContainer.maxButtonsReached'),
          '',
          {
            duration: 3000,
            verticalPosition: 'bottom',
            panelClass: ['snackbar-error'],
          }
        );
        return;
      }

      transferArrayItem(sourceData, targetData, event.previousIndex, event.currentIndex);

      // Update buttonCount for source row
      const sourceRow = this.sectionRows.find(
        (r): r is ButtonBarRow => r.type === 'button-bar' && r.items === sourceData
      );
      if (sourceRow) {
        sourceRow.buttonCount = sourceRow.items.length;
      }
      // Update buttonCount for target row
      targetRow.buttonCount = targetRow.items.length;

      // Remove empty button bars after transfer
      this.sectionRows = this.sectionRows.filter(
        r => r.type !== 'button-bar' || r.items.length > 0
      );
    }
    await this.updateLearningSection();
  }

  async dropContainer(event: CdkDragDrop<(LearningSectionContainerEntity | null)[]>) {
    if (event.previousContainer === event.container) {
      if (event.previousIndex === event.currentIndex) return;
      // Swap the two slots within the same row
      const data = event.container.data;
      const temp = data[event.previousIndex];
      data[event.previousIndex] = data[event.currentIndex];
      data[event.currentIndex] = temp;
    }
    else {
      const movedContainer = event.previousContainer.data[event.previousIndex];
      const targetData = event.container.data;

      // Find the best target slot: prefer currentIndex if null, otherwise first null slot
      let targetSlot = event.currentIndex;
      if (targetData[targetSlot] !== null) {
        const nullIdx = targetData.indexOf(null);
        if (nullIdx !== -1) {
          targetSlot = nullIdx;
        }
        else {
          // No null slot available – abort to avoid data loss
          return;
        }
      }

      // Replace source slot with null (keep the gap)
      event.previousContainer.data[event.previousIndex] = null;

      // Place container in the target slot
      targetData[targetSlot] = movedContainer;

      // Remove container rows where ALL slots are null (completely empty)
      this.sectionRows = this.sectionRows.filter(
        r => r.type !== 'containers' || r.containers.some(c => c !== null)
      );
    }

    // Strip null slots from rows that are full (real containers >= columnNumber)
    const maxCols = this.columnNumber || 1;
    for (const row of this.sectionRows) {
      if (row.type === 'containers') {
        const realCount = row.containers.filter(c => c !== null).length;
        if (realCount >= maxCols) {
          row.containers = row.containers.filter(c => c !== null);
        }
      }
    }

    const flatContainers = this.sectionRows
      .filter((r): r is ContainerRow => r.type === 'containers')
      .flatMap(r => r.containers)
      .filter((c): c is LearningSectionContainerEntity => c !== null);
    const normalizedContainers = flatContainers.map((c) => ({
      id: c.id!,
      type: 'learning-section-containers',
    }));

    this.form.get('learningSectionContainers')?.setValue(normalizedContainers);
    await this.updateLearningSection();
  }

  async onChangeLayout(selectedLayout: LearningSectionLayoutEntity) {
    if (!selectedLayout?.id) return;
    this.form.get('learningSectionLayout')?.setValue(selectedLayout.id);

    const oldMaxCols = this.columnNumber || 1;
    this.setLayout(selectedLayout);
    const newMaxCols = this.columnNumber || 1;

    this.adjustRowsForColumnChange(oldMaxCols, newMaxCols);

    const flatContainers = this.sectionRows
      .filter((r): r is ContainerRow => r.type === 'containers')
      .flatMap(r => r.containers)
      .filter((c): c is LearningSectionContainerEntity => c !== null);
    const normalizedContainers = flatContainers.map(c => ({
      id: c.id!,
      type: 'learning-section-containers',
    }));
    this.form.get('learningSectionContainers')?.setValue(normalizedContainers);

    await this.updateLearningSection();
  }

  /**
   * Adjusts container rows after the column count of the layout has changed
   * (e.g. switching between a single-column and a two-column layout).
   * Full-width rows are left untouched. Every other row's real containers are
   * re-chunked into groups of the new column count and padded with `null`
   * slots, so freed-up space gets a drop placeholder and overflowing
   * containers are split into additional rows.
   *
   * @param {number} oldMaxCols - The column count before the layout change.
   * @param {number} newMaxCols - The column count after the layout change.
   * @return {void}
   */
  private adjustRowsForColumnChange(oldMaxCols: number, newMaxCols: number): void {
    if (oldMaxCols === newMaxCols || newMaxCols < 1) return;

    const newRows: SectionRow[] = [];
    for (const row of this.sectionRows) {
      if (row.type !== 'containers' || this.isFullWidthRow(row)) {
        newRows.push(row);
        continue;
      }

      const realContainers = row.containers.filter(
        (c): c is LearningSectionContainerEntity => c !== null,
      );
      if (realContainers.length === 0) {
        newRows.push(row);
        continue;
      }

      for (let i = 0; i < realContainers.length; i += newMaxCols) {
        const chunk = realContainers.slice(i, i + newMaxCols);
        newRows.push({
          type: 'containers',
          containers: this.padContainersToMaxCols(chunk, newMaxCols),
        });
      }
    }

    this.sectionRows = newRows;
  }

  private serializeSectionRows(): SectionRowData[] {
    return this.sectionRows.map(row => {
      if (row.type === 'containers') {
        return {
          type: 'containers',
          containerIds: row.containers.map(c => c?.id ?? null),
        } as SectionRowContainerData;
      }
      return {
        type: 'button-bar',
        items: row.items.map(item => ({
          label: item.label,
          contentType: item.contentType ?? null,
          contentId: item.contentId ?? null,
        })),
      } as SectionRowButtonBarData;
    });
  }

  private deserializeSectionRows(data: SectionRowData[]): void {
    const containerState = this.store.selectSnapshot(
      this.learningSectionContainerService.stateClass
    ) as EntityStateModel<LearningSectionContainerEntity>;

    this.sectionRows = data.map(rowData => {
      if (rowData.type === 'containers') {
        const containers: (LearningSectionContainerEntity | null)[] = rowData.containerIds
          .map(id => id ? (containerState.entities[id] ?? null) : null);
        return { type: 'containers', containers } as ContainerRow;
      }
      return {
        type: 'button-bar',
        buttonCount: rowData.items.length,
        items: rowData.items.map(item => ({
          label: item.label,
          contentType: item.contentType ?? null,
          contentId: item.contentId ?? null,
        })),
      } as ButtonBarRow;
    });
  }

  private async updateLearningSection(): Promise<void> {
    if (!this.form.valid) return;

    const formValue = this.form.value;

    const updatedLearningSection = Object.assign(
      new LearningSectionEntity(),
      { id: this.data?.id },
      formValue,
      {
        consumer: formValue.consumer ?
          { type: 'consumers', id: formValue.consumer } :
          null,
        subjects: (formValue.subjects as string[])?.length ?
          (formValue.subjects as string[]).map((id) => ({
            id,
            type: 'subjects',
          })) :
          [],
        learningUnits: (formValue.learningUnits as string[])?.length ?
          (formValue.learningUnits as string[]).map((id) => ({
            id,
            type: 'learning-units',
          })) :
          [],
        learningSectionLayout: formValue.learningSectionLayout ?
          {
            type: 'learning-section-layouts',
            id: formValue.learningSectionLayout,
          } :
          null,
        learningSectionContainers: (
          formValue.learningSectionContainers as (
            | string |
            RelationRef<LearningSectionContainerEntity>
          )[]
        )?.length ?
          (
            formValue.learningSectionContainers as (
              | string |
              RelationRef<LearningSectionContainerEntity>
            )[]
          ).map((c) =>
            typeof c === 'string' ?
              { id: c, type: 'learning-section-containers' } :
              c,
          ) :
          [],
        sectionRows: this.serializeSectionRows(),
      },
    );

    await firstValueFrom(
      this.learningSectionService.update(updatedLearningSection),
    );
    this.store.dispatch(new InvalidateLoadedQueries());

    this.buttonItemContentData = {};
    await this.loadButtonItemContentData();
    this.cdr.markForCheck();
  }

  private setLayout(selectedLayout: LearningSectionLayoutEntity) {
    this.selectedLayoutOption = selectedLayout;
    this.columnNumber = selectedLayout.columns;
  }

  /**
   * Applies changes to a container's layout to make it either full-width or revert it back to its original layout.
   * Adjusts the rows and containers within the layout based on the specified full-width status.
   *
   * @param containerId - The unique identifier of the container whose layout is being changed.
   * @param isNowFullWidth - A boolean indicating whether the container is now in full-width mode (true) or not (false).
   * @return void
   */
  private applyFullWidthChange(containerId: string, isNowFullWidth: boolean): void {
    const maxCols = this.columnNumber || 1;
    // single-column layout: nothing to split
    if (maxCols <= 1) return;

    let rowIndex = -1;
    let slotIndex = -1;
    for (let i = 0; i < this.sectionRows.length; i++) {
      const row = this.sectionRows[i];
      if (row.type !== 'containers') continue;
      const idx = row.containers.findIndex(c => c?.id === containerId);
      if (idx !== -1) {
        rowIndex = i;
        slotIndex = idx;
        break;
      }
    }
    if (rowIndex === -1) return;
    const row = this.sectionRows[rowIndex] as ContainerRow;

    if (isNowFullWidth) {
      if (row.containers.length === 1) return; // already alone, CSS handles the rest
      const container = row.containers[slotIndex]!;
      const remaining = row.containers.filter((_, i) => i !== slotIndex);
      this.sectionRows.splice(rowIndex, 1, ...this.buildSplitRows(container, slotIndex, remaining, maxCols));
    }
    else {
      row.containers = this.padContainersToMaxCols(row.containers, maxCols);
    }
  }

  /**
   * Pads the given array of containers with `null` values until its length matches the specified maximum number of columns.
   *
   * @param {Array<LearningSectionContainerEntity | null>} containers - The array of containers to be padded. May include null values.
   * @param {number} maxCols - The maximum number of columns that the array should be padded to.
   * @return {Array<LearningSectionContainerEntity | null>} A new array containing the original containers, padded with `null` values if necessary.
   */
  private padContainersToMaxCols(
    containers: (LearningSectionContainerEntity | null)[],
    maxCols: number,
  ): (LearningSectionContainerEntity | null)[] {
    const padded = [...containers];
    while (padded.length < maxCols) {
      padded.push(null);
    }
    return padded;
  }

  /**
   * Builds and returns an array of `SectionRow` objects by organizing the provided container and remaining entities
   * into rows based on the specified number of maximum columns.
   *
   * @param {LearningSectionContainerEntity} container - The primary container entity to be used for creating the rows.
   * @param {number} slotIndex - The index indicating the position or order of the container within the row layout.
   * @param {(LearningSectionContainerEntity | null)[]} remaining - The array of remaining container entities or null values
   * representing empty slots that need to be organized into rows.
   * @param {number} maxCols - The maximum number of columns allowed per row.
   * @return {SectionRow[]} An array of `SectionRow` objects representing the organized rows of containers.
   */
  private buildSplitRows(
    container: LearningSectionContainerEntity,
    slotIndex: number,
    remaining: (LearningSectionContainerEntity | null)[],
    maxCols: number,
  ): SectionRow[] {
    const fullWidthRow: ContainerRow = { type: 'containers', containers: [container] };
    if (!remaining.some(c => c !== null)) return [fullWidthRow];

    const remainingRow: ContainerRow = { type: 'containers', containers: this.padContainersToMaxCols(remaining, maxCols) };
    return slotIndex === 0 ? [fullWidthRow, remainingRow] : [remainingRow, fullWidthRow];
  }

  /**
   * Normalizes section rows by splitting rows containing a full-width container alongside other containers.
   * Rearranges them into multiple rows based on the maximum column count.
   * Also pads rows that hold a single, non-full-width container with `null` slots, so old
   * data sets that are missing those empty slots still show a drop placeholder.
   * Conversely, a row that only holds a full-width container alongside `null` slots is
   * stripped down to just that container, since a full-width container must always stand
   * alone in its row without any `null` slots next to it.
   *
   * @return {boolean} Returns `true` if any rows were modified during normalization, otherwise `false`.
   */
  private normalizeFullWidthRows(): boolean {
    const maxCols = this.columnNumber || 1;
    if (maxCols <= 1) return false;

    let changed = false;
    const newRows: SectionRow[] = [];
    for (const row of this.sectionRows) {
      if (row.type !== 'containers') {
        newRows.push(row);
        continue;
      }
      if (row.containers.length <= 1) {
        const singleContainer = row.containers[0] ?? null;
        if (singleContainer && !singleContainer.fullWidth && row.containers.length < maxCols) {
          changed = true;
          newRows.push({ type: 'containers', containers: this.padContainersToMaxCols(row.containers, maxCols) });
        }
        else {
          newRows.push(row);
        }
        continue;
      }
      const fullWidthIndex = row.containers.findIndex(c => c?.fullWidth);
      if (fullWidthIndex === -1) {
        newRows.push(row);
        continue;
      }
      const hasNormalContainer = row.containers.some(c => c && !c.fullWidth);
      if (!hasNormalContainer) {
        // Full-width container with only `null` slots alongside it - it must stand alone.
        changed = true;
        newRows.push({ type: 'containers', containers: [row.containers[fullWidthIndex]!] });
        continue;
      }
      changed = true;
      const container = row.containers[fullWidthIndex]!;
      const remaining = row.containers.filter((_, i) => i !== fullWidthIndex);
      newRows.push(...this.buildSplitRows(container, fullWidthIndex, remaining, maxCols));
    }
    if (changed) this.sectionRows = newRows;
    return changed;
  }

  private isFullWidthRow(row: SectionRow): boolean {
    return (
      row.type === 'containers' &&
      row.containers.length === 1 &&
      !!row.containers[0]?.fullWidth
    );
  }

  get gridTemplateColumns(): string {
    const layout = this.selectedLayoutOption;
    if (
      !layout ||
      layout.columns === 1 ||
      Number(layout.percentageSplit) === 100
    ) {
      return '100%';
    }

    switch (Number(layout.percentageSplit)) {
      case 20: return 'repeat(5, 1fr)';
      case 25:
        return '1fr 3fr';
      case 50:
        return '1fr 1fr';
      case 75:
        return '3fr 1fr';
      case 80:
        return '4fr 1fr';
      case 90:
        return '90% 10%';
      default:
        return '1fr 1fr'; // fallback for unknown values with 2 columns
    }
  }

  getContentTypeLabel(type: string | undefined): string {
    const mapping: Record<string, string> = {
      'explanation-contents': this.translocoService.translate('contentType.editor'),
      'sequences': this.translocoService.translate('contentType.sequence'),
      'explanations': this.translocoService.translate('contentType.explanation'),
      'tests': this.translocoService.translate('contentType.test'),
      'learning-sections': this.translocoService.translate('contentType.learningSection'),
    };
    return type ? mapping[type] || type : '';
  }

  editButtonBarItem(row: ButtonBarRow, index: number): void {
    const item = row.items[index];
    if (!item) return;

    const dialogData: ButtonBarItemData = {
      label: item.label,
      contentType: item.contentType ?? null,
      contentId: item.contentId ?? null,
      consumerId: this.form.get('consumer')?.value ?? null,
    };

    const dialogRef = this.dialog.open(
      FormDialogButtonBarItemComponent,
      {
        width: '1200px',
        maxWidth: 'none',
        data: dialogData
      }
    );

    dialogRef.afterClosed()
      .pipe(
        filter((result): result is ButtonBarItemData => !!result),
        take(1)
      )
      .subscribe(async result => {
        row.items[index] = {
          label: result.label,
          contentType: result.contentType ?? null,
          contentId: result.contentId ?? null,
        };
        await this.updateLearningSection();
      });
  }

  editLearningSectionSingleContainer(
    container: LearningSectionContainerEntity,
  ) {
    if (!container?.id) return;

    const containerState = this.store.selectSnapshot(
      this.learningSectionContainerService.stateClass,
    ) as EntityStateModel<LearningSectionContainerEntity>;

    const hydratedContainer = containerState.entities[container.id];
    if (!hydratedContainer) return;

    const previousFullWidth = !!hydratedContainer.fullWidth;

    const dialogRef = this.dialog.open(
      FormDialogLearningSectionContainerContentComponent,
      {
        width: '1200px',
        maxWidth: 'none',
        data: hydratedContainer,
      },
    );

    dialogRef
      .afterClosed()
      .pipe(
        // Only proceed if a value was returned (not on cancel)
        filter(
          (updated): updated is LearningSectionContainerEntity => !!updated,
        ),
        take(1), // Clean up subscription
      )
      .subscribe(async (updated) => {
        // Refresh container references in sectionRows from the updated store
        const updatedState = this.store.selectSnapshot(
          this.learningSectionContainerService.stateClass
        ) as EntityStateModel<LearningSectionContainerEntity>;

        if (!!updated.fullWidth !== previousFullWidth) {
          this.applyFullWidthChange(container.id!, !!updated.fullWidth);
          await this.updateLearningSection();
        }

        for (const row of this.sectionRows) {
          if (row.type === 'containers') {
            row.containers = row.containers.map(c => c ? (updatedState.entities[c.id] ?? c) : null);
          }
        }

        // Re-run the name/type resolution for the UI labels
        this.preloadContainersWithContent();
        this.cdr.markForCheck();
      });
  }

  getPreviewLink(): string | null {
    const consumerId = this.form.get('consumer')?.value;
    if (!consumerId) return null;

    const consumerState = this.store.selectSnapshot(
      this.consumerService.stateClass,
    ) as EntityStateModel<ConsumerEntity>;
    const consumerEntity = consumerState.entities[consumerId];
    const consumerUrl = consumerEntity?.url;
    if (!consumerUrl) return null;

    const baseUrl = consumerUrl.replace(/\/+$/, '');
    const id = this.form.get('id')?.value;
    if (!id) return null;
    const learningSectionPreviewUrl = '/learning-section-preview/' + id;
    return (
      baseUrl +
      learningSectionPreviewUrl +
      '?token=' +
      environment.learningSectionPreviewToken
    );
  }

  async openPrint(template: string): Promise<void> {
    const id = this.form.get('id')?.value;
    if (!id) {
      return;
    }

    try {
      // Pass resolve_ids=1 so the backend auto-resolves module and unit IDs from the database.
      // The generic template does not need contextual metadata, so resolve_ids is omitted.
      const resolveIds = template !== 'generic' ? '&resolve_ids=1' : '';
      const token = this.store.selectSnapshot(AuthState.getToken);
      const response = await fetch(
        `${environment.backendUrl}/api/v2/learning-sections/${id}/print?template=${encodeURIComponent(template)}${resolveIds}`,
        {
          headers: {
            'Accept': 'application/pdf',
            // Required for Laravel to recognize this as an AJAX request (Sanctum cookie auth)
            'X-Requested-With': 'XMLHttpRequest',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
        }
      );

      // Explicitly set the MIME type so the browser recognizes the content as PDF
      // (response.blob() does not always inherit the Content-Type in cross-origin / Sanctum contexts)
      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');

      // Revoke the Blob URL after a short delay to free memory
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    }
    catch {
      this.showMessage(this.translocoService.translate('learningSectionContainer.error.printError'));
    }
  }

  private showMessage(
    message: string,
    messageTye: messageTypes = 'mtSuccess',
  ): void {
    this.widgetService.setMessage({
      message,
      messageTye,
    });
  }

  copyContainerIdToClipboard(containerId: string | undefined): void {
    if (!containerId) return;

    navigator.clipboard
      .writeText(containerId)
      .then(() => {
        this.showMessage('Container-ID in Zwischenablage kopiert');
      })
      .catch(() => {
        this.showMessage('Fehler beim Kopieren der Container-ID', 'mtError');
      });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  toggleContainerId(): void {
    this.showContainerId.update((value) => !value);
  }
}
